import React, {
	FormEvent,
	useCallback,
	useMemo,
	useRef,
	useState,
} from "react";
import { Link, useLocation, Location, Navigate } from "react-router";
import { Trans, useTranslation } from "react-i18next";
import { meetsPasswordRequirements } from "../../../helper/password-requirements";
import IconDone from "../../../dynamicicons/icon-done";
import VelavuButton from "../../../elements/velavu-button";
import VelavuInput from "../../../elements/velavu-input";
import VelavuNotice from "../../../elements/velavu-notice/velavu-notice";
import VelavuVerificationInput from "../../../elements/velavu-verification-input";
import AuthenticationButtonBar from "../authentication-button-bar";
import * as onboardingStyles from "../authentication.module.scss";
import * as styles from "./recover-reset.module.scss";
import { confirmResetPassword, resetPassword } from "aws-amplify/auth";
import whitelabel from "../../../whitelabel";

interface RecoverResetHistoryState {
	email: string;
}

export default function RecoverReset() {
	const { t } = useTranslation();
	const location = useLocation() as Location<RecoverResetHistoryState | null>;

	const [verificationCode, setVerificationCode] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");

	const [isComplete, setComplete] = useState(false);
	const [errorText, setErrorText] = useState<string | undefined>(undefined);

	const [isLoading, setLoading] = useState(false);

	const passwordInputRef = useRef<HTMLInputElement | null>(null);

	const submitResend = useCallback(() => {
		if (!location.state?.email) return;

		resetPassword({
			username: location.state.email,
		}).catch((error) => console.log(error.response));
	}, [location.state?.email]);

	const inputOK =
		verificationCode.length === 6 &&
		meetsPasswordRequirements(password) &&
		password === confirmPassword;

	const submitReset = useCallback(
		(event: FormEvent<HTMLFormElement>) => {
			event.preventDefault();

			//Ignoring if we're already complete or there is no email data
			if (isComplete || !location.state?.email) return;

			//Ignoring if the user hasn't properly entered their information
			if (!inputOK) return;

			setLoading(true);

			confirmResetPassword({
				username: location.state.email,
				confirmationCode: verificationCode,
				newPassword: password,
			})
				.then(() => setComplete(true))
				.catch((error) => {
					console.log(error.response);
					setErrorText(error.message);
				})
				.finally(() => setLoading(false));
		},
		[
			isComplete,
			location.state?.email,
			inputOK,
			verificationCode,
			password,
			setErrorText,
			setComplete,
		],
	);

	const resendLinkComponent = useMemo(
		() =>
			isLoading || isComplete ? (
				<span />
			) : (
				<button
					type="button"
					className={styles.resendLink}
					onClick={submitResend}
				/>
			),
		[isLoading, isComplete, submitResend],
	);

	//If we don't have any state, redirect back to the password recovery
	if (!location.state?.email) {
		return <Navigate to="/login/recover" replace />;
	}

	return (
		<form onSubmit={submitReset}>
			<p className={onboardingStyles.instructionsText}>
				{t("auth.recover_reset.title")}
			</p>

			<VelavuVerificationInput
				value={verificationCode}
				onChange={setVerificationCode}
				onComplete={() => passwordInputRef.current!.focus()}
				disabled={isLoading || isComplete}
			/>
			<Trans
				i18nKey="auth.recover_reset.resend_prompt"
				parent="span"
				className={styles.resend}
				components={{ link: resendLinkComponent }}
			/>

			<VelavuInput
				inputRef={passwordInputRef}
				value={password}
				onChange={setPassword}
				placeholder={t("auth.recover_reset.password_placeholder")}
				type="password"
				autoComplete="new-password"
				trailingIcon={
					meetsPasswordRequirements(password) ? (
						<IconDone
							color={whitelabel.palette.active900}
							size={16}
						/>
					) : undefined
				}
				disabled={isLoading || isComplete}
				fullWidth
				bottomPadding
			/>
			<VelavuInput
				value={confirmPassword}
				onChange={setConfirmPassword}
				placeholder={t(
					"auth.recover_reset.confirm_password_placeholder",
				)}
				type="password"
				autoComplete="new-password"
				trailingIcon={
					meetsPasswordRequirements(password) &&
					password === confirmPassword ? (
						<IconDone
							color={whitelabel.palette.active900}
							size={16}
						/>
					) : undefined
				}
				disabled={isLoading || isComplete}
				fullWidth
				bottomPadding
			/>

			{isComplete ? (
				<>
					<VelavuNotice
						className={onboardingStyles.noticeMargin}
						type="confirm"
					>
						{t("auth.recover_reset.success_message")}
					</VelavuNotice>

					<AuthenticationButtonBar
						primary={
							<Link className={styles.link} to="/login">
								<VelavuButton
									label={t("auth.recover_reset.return_login")}
									fullWidth
								/>
							</Link>
						}
					/>
				</>
			) : (
				<>
					{errorText ? (
						<VelavuNotice
							className={onboardingStyles.noticeMargin}
							type="error"
						>
							{errorText}
						</VelavuNotice>
					) : (
						<VelavuNotice
							className={onboardingStyles.noticeMargin}
							type="info"
						>
							{t("password_requirements")}
						</VelavuNotice>
					)}

					<AuthenticationButtonBar
						primary={
							<VelavuButton
								label={t("auth.recover_reset.reset_button")}
								disabled={isLoading || !inputOK}
								fullWidth
								submit
							/>
						}
						secondary={
							<Link className={styles.link} to="/login">
								{t("auth.recover_reset.return_login")}
							</Link>
						}
					/>
				</>
			)}
		</form>
	);
}
